<script setup>
import { computed, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import WameAuthorizationFrame from 'dashboard/routes/dashboard/settings/inbox/channels/WameAuthorizationFrame.vue';

const props = defineProps({
  teams: {
    type: Array,
    default: () => [],
  },
  existingChannels: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  // URL da página de autorização da WAME, devolvida pelo provisionamento.
  authorizationUrl: {
    type: String,
    default: '',
  },
  errorMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'connect',
  'use-existing',
  'authorized',
  'verify',
  'close',
]);

const dialogRef = ref(null);

const STEPS = [
  { key: 'connection', label: 'Canal' },
  { key: 'authorize', label: 'Autorização' },
  { key: 'done', label: 'Pronto' },
];

const stepIndex = ref(0);
// 'completo' percorre o assistente inteiro; 'autorizacao' abre só o passo da
// Meta, para um canal que já existe (Autorizar na Meta / Refazer autorização).
const mode = ref('completo');
const provider = ref('');
const inboxName = ref('');
const teamId = ref('');
const localError = ref('');

const currentStep = computed(() => STEPS[stepIndex.value].key);
const hasTeams = computed(() => props.teams.length > 0);
const userTeamId = computed(
  () => props.teams.find(team => team.origin_eligible)?.id ?? ''
);

watch(userTeamId, value => {
  if (!teamId.value) teamId.value = value;
});

const network = computed(() =>
  provider.value === 'instagram'
    ? {
        label: 'Instagram Direct',
        icon: 'i-ri-instagram-line',
        iconClass: 'bg-n-ruby-3 text-n-ruby-11',
        requirement: 'A conta precisa ser Business ou Creator.',
      }
    : {
        label: 'Messenger',
        icon: 'i-ri-messenger-line',
        iconClass: 'bg-n-blue-3 text-n-blue-11',
        requirement: 'Você precisa ser administrador da Página.',
      }
);

const reset = () => {
  stepIndex.value = 0;
  mode.value = 'completo';
  provider.value = '';
  inboxName.value = '';
  teamId.value = userTeamId.value;
  localError.value = '';
};

const goTo = key => {
  const index = STEPS.findIndex(step => step.key === key);
  if (index >= 0) stepIndex.value = index;
};

const open = ({ provider: initialProvider = '' } = {}) => {
  reset();
  provider.value = initialProvider;
  inboxName.value = initialProvider === 'instagram' ? 'Instagram' : 'Messenger';
  goTo('connection');
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

/** Retorno do Instagram, que sai do iframe e volta pela querystring. */
const openAtResult = ({ provider: returnedProvider }) => {
  reset();
  provider.value = returnedProvider;
  goTo('done');
  dialogRef.value?.open();
};

const showAuthorization = () => {
  localError.value = '';
  goTo('authorize');
};

/**
 * Abre direto na autorização, para um canal que já existe.
 *
 * É o caso de "Autorizar na Meta" e "Refazer autorização": a conexão já foi
 * escolhida quando o canal nasceu, então as duas primeiras etapas não têm o que
 * perguntar.
 */
const openAtAuthorization = ({ provider: channelProvider }) => {
  reset();
  mode.value = 'autorizacao';
  provider.value = channelProvider;
  goTo('authorize');
  dialogRef.value?.open();
};

defineExpose({
  open,
  close,
  openAtResult,
  openAtAuthorization,
  showAuthorization,
});

const goBack = () => {
  localError.value = '';
  // Num canal que já existe não há etapa anterior: a conexão foi escolhida
  // quando ele nasceu, e voltar para aquela lista ofereceria uma decisão que
  // não está mais em aberto.
  if (mode.value === 'autorizacao') {
    close();
    return;
  }
  if (stepIndex.value === 0 || currentStep.value === 'done') {
    close();
    return;
  }
  stepIndex.value -= 1;
};

const advance = () => {
  if (currentStep.value === 'connection') {
    if (!inboxName.value.trim()) {
      localError.value = 'Informe um nome para a caixa de entrada.';
      return;
    }
    if (hasTeams.value && !teamId.value) {
      localError.value = 'Escolha a equipe que atenderá este canal.';
      return;
    }
    localError.value = '';
    emit('connect', {
      provider: provider.value,
      name: inboxName.value.trim(),
      teamId: hasTeams.value ? Number(teamId.value) : null,
    });
    return;
  }

  if (currentStep.value === 'authorize') {
    emit('verify');
    return;
  }

  close();
};

const primaryLabel = computed(() => {
  if (currentStep.value === 'connection') return 'Conectar';
  if (currentStep.value === 'authorize') return 'Já autorizei, verificar';
  if (currentStep.value === 'done') return 'Concluir';
  return 'Continuar';
});

const primaryDisabled = computed(() => {
  if (currentStep.value === 'authorize') return !props.authorizationUrl;
  return false;
});

const onAuthorized = () => {
  goTo('done');
  emit('authorized');
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <Dialog
    ref="dialogRef"
    class="social-wizard-dialog max-md:!my-4 max-md:!max-h-[calc(100dvh_-_2rem)] max-md:!overflow-hidden"
    width="xl"
    position="top"
    overflow-y-auto
    show-close-button
    mobile-inset
    panel-class="max-md:max-h-[calc(100dvh_-_2rem)] max-md:gap-3 max-md:overflow-hidden max-md:p-4"
    @close="emit('close')"
  >
    <div class="social-wizard flex flex-col gap-5">
      <ol
        v-if="mode === 'completo'"
        class="social-wizard__steps flex items-center gap-2"
      >
        <li
          v-for="(step, index) in STEPS"
          :key="step.key"
          class="social-wizard__step flex flex-1 items-center gap-2"
        >
          <span
            class="social-wizard__step-badge flex size-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors"
            :class="
              index <= stepIndex
                ? 'bg-n-brand text-white'
                : 'bg-n-alpha-2 text-n-slate-10'
            "
          >
            <i v-if="index < stepIndex" class="i-lucide-check text-sm" />
            <template v-else>{{ index + 1 }}</template>
          </span>
          <span
            class="social-wizard__step-label truncate text-xs font-medium"
            :class="index <= stepIndex ? 'text-n-slate-12' : 'text-n-slate-10'"
          >
            {{ step.label }}
          </span>
          <span
            v-if="index < STEPS.length - 1"
            class="social-wizard__step-line h-px flex-1 bg-n-weak"
            aria-hidden="true"
          />
        </li>
      </ol>

      <!-- O canal já vem da aba ativa; não há escolha de outra rede ou WhatsApp. -->
      <div
        v-if="currentStep === 'connection'"
        class="social-wizard__pane flex flex-col gap-4"
      >
        <div class="flex flex-col items-center gap-2 text-center">
          <div
            class="flex size-12 items-center justify-center rounded-2xl"
            :class="network.iconClass"
          >
            <i :class="network.icon" class="text-2xl" />
          </div>
          <h3 class="text-lg font-semibold text-n-slate-12">
            Conectar {{ network.label }}
          </h3>
          <p class="text-sm text-n-slate-11">
            Uma caixa de entrada exclusiva será criada para este canal.
            {{ network.requirement }}
          </p>
          <p class="text-xs leading-5 text-n-slate-10">
            Uma conexão protegida é reservada automaticamente. Nenhum WhatsApp
            pessoal precisa ser escolhido ou compartilhado.
          </p>
        </div>

        <div
          v-if="existingChannels.length"
          class="flex flex-col gap-2 rounded-2xl border border-n-weak bg-n-alpha-1 p-3"
        >
          <span class="text-sm font-semibold text-n-slate-12">
            Usar uma inbox existente
          </span>
          <p class="text-xs leading-5 text-n-slate-11">
            Retome uma caixa já criada sem perder seu histórico ou seus membros.
          </p>
          <button
            v-for="channel in existingChannels"
            :key="channel.inbox_id"
            type="button"
            class="flex items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-background px-3 py-2.5 text-left hover:border-n-brand"
            @click="emit('use-existing', channel.inbox_id)"
          >
            <span class="min-w-0 truncate text-sm font-medium text-n-slate-12">
              {{ channel.name }}
            </span>
            <span class="text-xs text-n-brand">Usar esta inbox</span>
          </button>
          <div class="flex items-center gap-3 py-1 text-xs text-n-slate-10">
            <span class="h-px flex-1 bg-n-weak" />
            ou criar uma nova
            <span class="h-px flex-1 bg-n-weak" />
          </div>
        </div>

        <label class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            Nome da caixa de entrada
          </span>
          <input
            v-model="inboxName"
            class="rounded-xl border border-n-weak bg-n-background px-3 py-2.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            maxlength="60"
            :placeholder="network.label"
          />
        </label>

        <label v-if="hasTeams" class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            Equipe responsável
          </span>
          <select
            v-model="teamId"
            class="rounded-xl border border-n-weak bg-n-background px-3 py-2.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option value="">Selecione uma equipe</option>
            <option v-for="team in teams" :key="team.id" :value="team.id">
              {{ team.name }} ({{ team.member_count }} membros)
            </option>
          </select>
          <span class="text-xs leading-5 text-n-slate-11">
            Todos os membros da equipe terão acesso a esta inbox. Não há vínculo
            com um vendedor específico.
          </span>
        </label>
      </div>

      <!-- Etapa 3 — autorização, embutida -->
      <div
        v-else-if="currentStep === 'authorize'"
        class="social-wizard__pane flex flex-col gap-4"
      >
        <div class="flex flex-col items-center gap-2 text-center">
          <div
            class="flex size-12 items-center justify-center rounded-2xl"
            :class="network.iconClass"
          >
            <i class="i-lucide-shield-check text-2xl" />
          </div>
          <h3 class="text-lg font-semibold text-n-slate-12">
            Autorize no {{ network.label }}
          </h3>
          <p class="text-sm text-n-slate-11">
            A autorização é da Meta e acontece aqui dentro. Ao terminar, esta
            janela avança sozinha e a caixa de entrada passa a receber.
          </p>
        </div>

        <WameAuthorizationFrame
          v-if="authorizationUrl"
          :url="authorizationUrl"
          :provider="provider"
          @connected="onAuthorized"
        />
        <p v-else class="text-sm text-n-slate-11">Preparando a autorização…</p>
      </div>

      <!-- Etapa 4 — confirmação -->
      <div v-else class="social-wizard__pane flex flex-col gap-4">
        <div class="flex flex-col items-center gap-2 text-center">
          <div
            class="flex size-12 items-center justify-center rounded-2xl bg-n-teal-3 text-n-teal-11"
          >
            <i class="i-lucide-check-circle-2 text-2xl" />
          </div>
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{ network.label }} autorizado
          </h3>
          <p class="text-sm text-n-slate-11">
            A caixa de entrada já existe e o canal passa a receber. A conta é
            identificada na primeira mensagem que chegar — o estado desta tela
            se atualiza sozinho quando isso acontecer.
          </p>
        </div>
      </div>

      <p
        v-if="localError || errorMessage"
        class="social-wizard__error flex items-start gap-2 text-xs font-medium text-n-ruby-11"
      >
        <i class="i-lucide-circle-alert mt-0.5 text-sm" />
        {{ localError || errorMessage }}
      </p>
    </div>

    <template #footer>
      <div class="social-wizard__footer flex items-center gap-3">
        <Button
          type="button"
          variant="faded"
          color="slate"
          class="social-wizard__back w-full"
          :label="
            currentStep === 'done' || mode === 'autorizacao'
              ? 'Fechar'
              : 'Voltar'
          "
          @click="goBack"
        />
        <Button
          type="button"
          class="social-wizard__advance w-full"
          :label="primaryLabel"
          :is-loading="isLoading"
          :disabled="isLoading || primaryDisabled"
          @click="advance"
        />
      </div>
    </template>
  </Dialog>
</template>
